import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Leaf, ArrowLeft, Plus, Trash2, Users, Save } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import MyPurchases from '@/components/MyPurchases';
import { toast } from 'sonner';

export default function FamilyPage() {
  const { user, updateChildren } = useAuth();
  const [draft, setDraft] = useState((user?.children || []).map(c => ({ ...c })));
  const [saving, setSaving] = useState(false);

  const updateAt = (idx, field, value) => {
    setDraft(d => d.map((c, i) => i === idx ? { ...c, [field]: field === 'year_level' ? Number(value) : value } : c));
  };

  const addChild = () => setDraft(d => [...d, { name: '', year_level: 5 }]);
  const removeAt = (idx) => setDraft(d => d.filter((_, i) => i !== idx));

  const save = async () => {
    const trimmed = draft.map(c => ({ name: (c.name || '').trim(), year_level: c.year_level || 5 }));
    if (trimmed.length === 0) { toast.error('Add at least one child'); return; }
    if (trimmed.some(c => !c.name)) { toast.error('Every child needs a name'); return; }
    const names = trimmed.map(c => c.name);
    if (new Set(names).size !== names.length) { toast.error('Names must be unique'); return; }
    setSaving(true);
    try {
      await updateChildren(trimmed);
      toast.success('Family updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center">
            <BrandMark size={36} />
          </Link>
          <Link to="/dashboard" data-testid="back-from-family">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-fern rounded-lg gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark flex items-center gap-3">
            <Users className="w-8 h-8 text-fern" /> Family
          </h1>
          <p className="text-slate-500 mt-2">Manage the children in your homeschool. Each child has their own progress, evidence, and reports.</p>
          {user?.family_name && <p className="text-sm text-fern mt-1">{user.family_name} whānau</p>}
        </div>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl">
          <CardContent className="p-6 sm:p-8 space-y-4">
            {draft.length === 0 && (
              <p className="text-sm text-slate-400 italic">No children yet. Add your first below.</p>
            )}
            {draft.map((c, idx) => (
              <div key={idx} data-testid={`child-row-${idx}`} className="flex items-end gap-3 p-4 bg-fern-50/40 rounded-2xl">
                <div className="flex-1">
                  <label className="text-xs uppercase tracking-wider text-fern-dark font-semibold">Name</label>
                  <Input
                    data-testid={`child-name-${idx}`}
                    value={c.name}
                    onChange={e => updateAt(idx, 'name', e.target.value)}
                    placeholder="e.g. Maia"
                    className="mt-1 rounded-xl bg-white"
                  />
                </div>
                <div className="w-24">
                  <label className="text-xs uppercase tracking-wider text-fern-dark font-semibold">Year</label>
                  <Input
                    data-testid={`child-year-${idx}`}
                    type="number"
                    min="1"
                    max="13"
                    value={c.year_level}
                    onChange={e => updateAt(idx, 'year_level', e.target.value)}
                    className="mt-1 rounded-xl bg-white text-center"
                  />
                </div>
                <Button
                  data-testid={`remove-child-${idx}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAt(idx)}
                  disabled={draft.length === 1}
                  className="text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl mb-1"
                  aria-label="Remove child"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}

            <Button
              data-testid="add-child-btn"
              variant="outline"
              onClick={addChild}
              className="w-full rounded-2xl border-dashed border-fern/30 text-fern hover:bg-fern-50 gap-2"
            >
              <Plus className="w-4 h-4" /> Add Another Child
            </Button>

            <div className="flex justify-end pt-4 border-t border-slate-100">
              <Button
                data-testid="save-family-btn"
                onClick={save}
                disabled={saving}
                className="bg-fern hover:bg-fern-light text-white rounded-full px-8 gap-2"
              >
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Family'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <MyPurchases />
      </main>
    </div>
  );
}
